export const SITE_TYPES = {
  APARTMENT: 'apartment',
  VILLA: 'villa',
  MIXED: 'mixed',
};

export const STRUCTURE_KINDS = {
  APARTMENT_BLOCK: 'apartmentBlock',
  VILLA_STREET: 'villaStreet',
};

export const VILLA_GROUPINGS = {
  STREET: 'street',
  ZONE: 'zone',
  PHASE: 'phase',
  CLUSTER: 'cluster',
};

export const FLAT_PATTERNS = {
  BLOCK_DASH_UNIT: 'blockDashUnit',
  BLOCK_UNIT: 'blockUnit',
  UNIT_ONLY: 'unitOnly',
  BLOCK_FLOOR_DASH_UNIT: 'blockFloorDashUnit',
  TOWER_BLOCK_DASH_UNIT: 'towerBlockDashUnit',
};

export const createBlockCustomFeature = (name, count = 1) => ({
  id: `feature_${Date.now()}`,
  name: name.trim(),
  count,
});

export const AMENITY_CATEGORIES = {
  CLUB_HOUSE: 'clubHouse',
  OUTDOOR: 'outdoor',
  UTILITY: 'utility',
  PARKING_ACCESS: 'parkingAccess',
};

const LETTERS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';

export const villaGroupingLabel = grouping => {
  switch (grouping) {
    case VILLA_GROUPINGS.ZONE:
      return 'Zone';
    case VILLA_GROUPINGS.PHASE:
      return 'Phase';
    case VILLA_GROUPINGS.CLUSTER:
      return 'Cluster';
    default:
      return 'Street';
  }
};

export const structureNameForIndex = (index, siteType, grouping = VILLA_GROUPINGS.STREET) => {
  const suffix = index < LETTERS.length ? LETTERS[index] : `${index + 1}`;
  if (siteType === SITE_TYPES.VILLA) {
    return `${villaGroupingLabel(grouping)} ${suffix}`;
  }
  return `Block ${suffix}`;
};

export const defaultApartmentBlock = index => ({
  name: structureNameForIndex(index, SITE_TYPES.APARTMENT),
  structureKind: STRUCTURE_KINDS.APARTMENT_BLOCK,
  blockType: 'residential',
  villaGrouping: null,
  floors: 10,
  basementFloors: 0,
  flatsPerFloor: 4,
  startingFlatNumber: 101,
  blockPrefix: '',
  flatPattern: FLAT_PATTERNS.BLOCK_DASH_UNIT,
  liftCount: 1,
  staircaseCount: 1,
  corridorCount: 1,
  parkingFloors: 0,
  terraceAccess: false,
  privateGarden: false,
  privateParking: false,
  backyardArea: false,
  waterMeter: false,
  sewageConnection: false,
  solarConnection: false,
  customFeatures: [],
  customUnitLabels: {},
  removedFlatKeys: [],
  extraFlatsPerFloor: {},
});

export const defaultVillaStreet = (index, grouping = VILLA_GROUPINGS.STREET) => ({
  name: structureNameForIndex(index, SITE_TYPES.VILLA, grouping),
  structureKind: STRUCTURE_KINDS.VILLA_STREET,
  blockType: 'residential',
  villaGrouping: grouping,
  floors: 1,
  basementFloors: 0,
  flatsPerFloor: 25,
  startingFlatNumber: 1,
  blockPrefix: '',
  flatPattern: FLAT_PATTERNS.BLOCK_DASH_UNIT,
  liftCount: 1,
  staircaseCount: 1,
  corridorCount: 1,
  parkingFloors: 0,
  terraceAccess: false,
  privateGarden: false,
  privateParking: false,
  backyardArea: false,
  waterMeter: false,
  sewageConnection: false,
  solarConnection: false,
  customFeatures: [],
  customUnitLabels: {},
});

export const isVillaStreet = block => block.structureKind === STRUCTURE_KINDS.VILLA_STREET;

export const getRemovedFlatKeys = block => block.removedFlatKeys || [];

export const isFlatRemoved = (block, floor, flatIndexOnFloor) =>
  getRemovedFlatKeys(block).includes(flatUnitKey(floor, flatIndexOnFloor));

export const getExtraFlatsOnFloor = (block, floor) => {
  const extras = block.extraFlatsPerFloor || {};
  return Number(extras[String(floor)] ?? extras[floor] ?? 0) || 0;
};

export const getFlatIndicesOnFloor = (block, floor) => {
  const indices = [];
  for (let i = 1; i <= block.flatsPerFloor; i += 1) {
    if (!isFlatRemoved(block, floor, i)) indices.push(i);
  }
  const extraCount = getExtraFlatsOnFloor(block, floor);
  for (let i = 1; i <= extraCount; i += 1) {
    const idx = block.flatsPerFloor + i;
    if (!isFlatRemoved(block, floor, idx)) indices.push(idx);
  }
  return indices;
};

export const removeFlatFromBlock = (block, floor, flatIndexOnFloor) => {
  const key = flatUnitKey(floor, flatIndexOnFloor);
  const removed = new Set(getRemovedFlatKeys(block));
  removed.add(key);

  const overrides = { ...(block.customUnitLabels || {}) };
  delete overrides[key];

  return {
    ...block,
    removedFlatKeys: [...removed],
    customUnitLabels: overrides,
  };
};

export const addFlatToBlock = (block, floor) => {
  const extras = { ...(block.extraFlatsPerFloor || {}) };
  const floorKey = String(floor);
  extras[floorKey] = getExtraFlatsOnFloor(block, floor) + 1;
  return { ...block, extraFlatsPerFloor: extras };
};

export const pruneBlockFlatLayout = block => {
  const floors = block.floors || 0;
  const perFloor = block.flatsPerFloor || 0;
  const removed = getRemovedFlatKeys(block).filter(key => {
    const match = /^f:(\d+):(\d+)$/.exec(key);
    if (!match) return false;
    const f = Number(match[1]);
    const i = Number(match[2]);
    if (f > floors) return false;
    const maxIdx = perFloor + getExtraFlatsOnFloor(block, f);
    return i <= maxIdx;
  });

  const extras = {};
  for (let f = 1; f <= floors; f += 1) {
    const count = getExtraFlatsOnFloor(block, f);
    if (count > 0) extras[String(f)] = count;
  }

  const overrides = { ...(block.customUnitLabels || {}) };
  Object.keys(overrides).forEach(key => {
    const match = /^f:(\d+):(\d+)$/.exec(key);
    if (!match) return;
    const f = Number(match[1]);
    const i = Number(match[2]);
    const maxIdx = perFloor + (extras[String(f)] ?? 0);
    if (f > floors || i > maxIdx) {
      delete overrides[key];
    }
  });

  return {
    ...block,
    removedFlatKeys: removed,
    extraFlatsPerFloor: extras,
    customUnitLabels: overrides,
  };
};

export const blockTotalUnits = block => {
  if (isVillaStreet(block)) return block.flatsPerFloor;
  let total = 0;
  for (let f = 1; f <= block.floors; f += 1) {
    total += getFlatIndicesOnFloor(block, f).length;
  }
  return total;
};

export const totalApartmentUnits = blocks =>
  blocks.filter(b => !isVillaStreet(b)).reduce((sum, b) => sum + blockTotalUnits(b), 0);

export const totalVillaUnits = blocks =>
  blocks.filter(b => isVillaStreet(b)).reduce((sum, b) => sum + blockTotalUnits(b), 0);

export const totalFlatsForSite = blocks =>
  blocks.reduce((sum, b) => sum + blockTotalUnits(b), 0);

export const resolveBlockCode = (blockName, blockPrefix) => {
  const trimmed = (blockPrefix || '').trim();
  if (trimmed) return trimmed;
  const parts = (blockName || '').trim().split(/\s+/);
  const last = parts[parts.length - 1] || '';
  if (last.length === 1 && /^[A-Za-z]$/.test(last)) return last.toUpperCase();
  return last.length >= 1 ? last[0].toUpperCase() : 'A';
};

export const flatPatternLabel = (pattern, blockCode = 'A') => {
  switch (pattern) {
    case FLAT_PATTERNS.BLOCK_UNIT:
      return `${blockCode}101`;
    case FLAT_PATTERNS.UNIT_ONLY:
      return '101';
    case FLAT_PATTERNS.BLOCK_FLOOR_DASH_UNIT:
      return `${blockCode}1-01`;
    case FLAT_PATTERNS.TOWER_BLOCK_DASH_UNIT:
      return `Tower${blockCode}-101`;
    default:
      return `${blockCode}-101`;
  }
};

export const villaPatternLabel = (pattern, streetCode = 'A') => {
  switch (pattern) {
    case FLAT_PATTERNS.BLOCK_UNIT:
      return `${streetCode}001`;
    case FLAT_PATTERNS.UNIT_ONLY:
      return '001';
    case FLAT_PATTERNS.BLOCK_FLOOR_DASH_UNIT:
      return `${streetCode}-01`;
    case FLAT_PATTERNS.TOWER_BLOCK_DASH_UNIT:
      return `Tower${streetCode}-001`;
    default:
      return `${streetCode}-001`;
  }
};

const unitNumber = (floor, flatIndexOnFloor, startingFlatNumber) => {
  const startSuffix = startingFlatNumber % 100;
  if (startSuffix === 0) return floor * 100 + flatIndexOnFloor;
  return floor * 100 + startSuffix + flatIndexOnFloor - 1;
};

export const generateFlatNumber = ({
  pattern,
  blockName,
  blockPrefix = '',
  floor,
  flatIndexOnFloor,
  startingFlatNumber,
}) => {
  const code = resolveBlockCode(blockName, blockPrefix);
  const unit = unitNumber(floor, flatIndexOnFloor, startingFlatNumber);
  const flatOnFloor = String(flatIndexOnFloor).padStart(2, '0');

  switch (pattern) {
    case FLAT_PATTERNS.BLOCK_UNIT:
      return `${code}${unit}`;
    case FLAT_PATTERNS.UNIT_ONLY:
      return String(unit);
    case FLAT_PATTERNS.BLOCK_FLOOR_DASH_UNIT:
      return `${code}${floor}-${flatOnFloor}`;
    case FLAT_PATTERNS.TOWER_BLOCK_DASH_UNIT:
      return `Tower${code}-${unit}`;
    default:
      return `${code}-${unit}`;
  }
};

const villaSequentialNumber = (villaIndex, startingNumber) => startingNumber + villaIndex - 1;

const formatVillaUnit = (unit, pattern) => {
  switch (pattern) {
    case FLAT_PATTERNS.UNIT_ONLY:
      return String(unit);
    case FLAT_PATTERNS.BLOCK_FLOOR_DASH_UNIT:
      return String(unit).padStart(2, '0');
    default:
      return String(unit).padStart(3, '0');
  }
};

export const generateVillaNumber = ({
  pattern,
  streetName,
  streetPrefix = '',
  villaIndex,
  startingVillaNumber,
}) => {
  const code = resolveBlockCode(streetName, streetPrefix);
  const unit = villaSequentialNumber(villaIndex, startingVillaNumber);
  const formatted = formatVillaUnit(unit, pattern);

  switch (pattern) {
    case FLAT_PATTERNS.BLOCK_UNIT:
      return `${code}${formatted}`;
    case FLAT_PATTERNS.UNIT_ONLY:
      return formatted;
    case FLAT_PATTERNS.BLOCK_FLOOR_DASH_UNIT:
      return `${code}-${formatted}`;
    case FLAT_PATTERNS.TOWER_BLOCK_DASH_UNIT:
      return `Tower${code}-${formatted}`;
    default:
      return `${code}-${formatted}`;
  }
};

export const flatUnitKey = (floor, flatIndexOnFloor) => `f:${floor}:${flatIndexOnFloor}`;

export const villaUnitKey = villaIndex => `v:${villaIndex}`;

export const PREVIEW_EXPANSION = {
  COLLAPSED: 'collapsed',
  PARTIAL: 'partial',
  EXPANDED: 'expanded',
};

export const isMorePreviewLabel = label => String(label).startsWith('+');

export const nextPreviewExpansion = current => {
  switch (current) {
    case PREVIEW_EXPANSION.COLLAPSED:
      return PREVIEW_EXPANSION.PARTIAL;
    case PREVIEW_EXPANSION.PARTIAL:
    case PREVIEW_EXPANSION.EXPANDED:
    default:
      return PREVIEW_EXPANSION.EXPANDED;
  }
};

export const visiblePreviewCount = ({ total, maxCollapsed, expansion }) => {
  if (total <= maxCollapsed) return total;
  const hidden = total - maxCollapsed;
  switch (expansion) {
    case PREVIEW_EXPANSION.COLLAPSED:
      return maxCollapsed;
    case PREVIEW_EXPANSION.PARTIAL:
      return maxCollapsed + Math.floor(hidden / 2);
    case PREVIEW_EXPANSION.EXPANDED:
    default:
      return total;
  }
};

export const generatedFlatLabel = (block, floor, flatIndexOnFloor) =>
  generateFlatNumber({
    pattern: block.flatPattern,
    blockName: block.name,
    blockPrefix: block.blockPrefix,
    floor,
    flatIndexOnFloor,
    startingFlatNumber: block.startingFlatNumber,
  });

export const generatedVillaLabel = (street, villaIndex) =>
  generateVillaNumber({
    pattern: street.flatPattern,
    streetName: street.name,
    streetPrefix: street.blockPrefix,
    villaIndex,
    startingVillaNumber: street.startingFlatNumber,
  });

export const resolveVillaLabel = (street, villaIndex) => {
  const key = villaUnitKey(villaIndex);
  const override = street.customUnitLabels?.[key]?.trim();
  if (override) return override;
  return generatedVillaLabel(street, villaIndex);
};

export const resolveFlatLabel = (block, floor, flatIndexOnFloor) => {
  const key = flatUnitKey(floor, flatIndexOnFloor);
  const override = block.customUnitLabels?.[key]?.trim();
  if (override) return override;
  return generatedFlatLabel(block, floor, flatIndexOnFloor);
};

export const validateFlatLabelChange = (block, floor, flatIndexOnFloor, newLabel) => {
  const trimmed = newLabel.trim();
  if (!trimmed) return 'Unit number cannot be empty';

  const counts = {};
  for (let f = 1; f <= block.floors; f += 1) {
    for (const i of getFlatIndicesOnFloor(block, f)) {
      const label =
        f === floor && i === flatIndexOnFloor ? trimmed : resolveFlatLabel(block, f, i);
      counts[label] = (counts[label] || 0) + 1;
    }
  }

  if ((counts[trimmed] || 0) > 1) return `${trimmed} exists`;
  return null;
};

export const applyFlatLabelOverride = (block, floor, flatIndexOnFloor, newLabel) => {
  const trimmed = newLabel.trim();
  const key = flatUnitKey(floor, flatIndexOnFloor);
  const overrides = { ...(block.customUnitLabels || {}) };
  const generated = generatedFlatLabel(block, floor, flatIndexOnFloor);

  if (trimmed === generated) {
    delete overrides[key];
  } else {
    overrides[key] = trimmed;
  }

  return { ...block, customUnitLabels: overrides };
};

export const validateVillaLabelChange = (street, villaIndex, newLabel) => {
  const trimmed = newLabel.trim();
  if (!trimmed) return 'Unit number cannot be empty';

  const counts = {};
  for (let i = 1; i <= street.flatsPerFloor; i += 1) {
    const label = i === villaIndex ? trimmed : resolveVillaLabel(street, i);
    counts[label] = (counts[label] || 0) + 1;
  }

  if ((counts[trimmed] || 0) > 1) return `${trimmed} exists`;
  return null;
};

export const applyVillaLabelOverride = (street, villaIndex, newLabel) => {
  const trimmed = newLabel.trim();
  const key = villaUnitKey(villaIndex);
  const overrides = { ...(street.customUnitLabels || {}) };
  const generated = generatedVillaLabel(street, villaIndex);

  if (trimmed === generated) {
    delete overrides[key];
  } else {
    overrides[key] = trimmed;
  }

  return { ...street, customUnitLabels: overrides };
};

export const generateFloorPreviews = (
  block,
  { maxFloors = 3, expansion = PREVIEW_EXPANSION.COLLAPSED } = {}
) => {
  if (!block.floors || !block.flatsPerFloor) return [];

  const previews = [];
  const floorsToShow = Math.min(
    block.floors,
    Math.max(
      1,
      visiblePreviewCount({
        total: block.floors,
        maxCollapsed: maxFloors,
        expansion,
      })
    )
  );

  for (let f = 1; f <= floorsToShow; f += 1) {
    const flatIndices = getFlatIndicesOnFloor(block, f);
    const flats = flatIndices.map(i => resolveFlatLabel(block, f, i));
    previews.push({ floor: f, flats, flatIndices });
  }

  const hiddenFloors = block.floors - floorsToShow;
  if (hiddenFloors > 0) {
    previews.push({ floor: -1, flats: [`+ ${hiddenFloors} more floors…`] });
  }

  return previews;
};

export const generateVillaPreviews = (
  street,
  { maxVillas = 8, expansion = PREVIEW_EXPANSION.COLLAPSED } = {}
) => {
  if (!street.flatsPerFloor) return [];

  const villas = [];
  const count = Math.min(
    street.flatsPerFloor,
    Math.max(
      1,
      visiblePreviewCount({
        total: street.flatsPerFloor,
        maxCollapsed: maxVillas,
        expansion,
      })
    )
  );

  for (let i = 1; i <= count; i += 1) {
    villas.push(resolveVillaLabel(street, i));
  }

  const hiddenVillas = street.flatsPerFloor - count;
  if (hiddenVillas > 0) {
    villas.push(`+ ${hiddenVillas} more villas…`);
  }

  return villas;
};

export const allFlatUnitLabelsForBlock = block => {
  if (isVillaStreet(block)) {
    return Array.from({ length: block.flatsPerFloor }, (_, i) =>
      resolveVillaLabel(block, i + 1)
    );
  }
  const labels = [];
  for (let f = 1; f <= block.floors; f += 1) {
    for (const i of getFlatIndicesOnFloor(block, f)) {
      labels.push(resolveFlatLabel(block, f, i));
    }
  }
  return labels;
};

export const structureSummaryLabel = (siteType, count, totalUnits) => {
  if (siteType === SITE_TYPES.VILLA) {
    const streetLabel = count === 1 ? 'street' : 'streets';
    return `${count} ${streetLabel} · ${totalUnits} villas`;
  }
  if (siteType === SITE_TYPES.MIXED) {
    return `${count} structures · ${totalUnits} dwelling units`;
  }
  const blockLabel = count === 1 ? 'block' : 'blocks';
  return `${count} ${blockLabel} · ${totalUnits} flats`;
};

export const mixedStructureSummaryLabel = ({
  blockCount,
  streetCount,
  apartmentUnits,
  villaUnits,
}) => {
  const parts = [];
  if (blockCount > 0) parts.push(`${blockCount} ${blockCount === 1 ? 'block' : 'blocks'}`);
  if (streetCount > 0) parts.push(`${streetCount} ${streetCount === 1 ? 'street' : 'streets'}`);
  return `${parts.join(' · ')} · ${apartmentUnits + villaUnits} dwelling units`;
};

export const buildAmenityLocationOptions = (blocks, clubHouse = null) => {
  const options = ['Entire Site'];
  if (clubHouse?.floors > 0) {
    options.push(clubHouse.name || 'Club House');
    for (let i = 0; i < clubHouse.floors; i += 1) {
      options.push(`${clubHouse.name || 'Club House'} — Floor ${i + 1}`);
    }
  }
  blocks.forEach(block => {
    options.push(block.name);
    if (!isVillaStreet(block)) {
      for (let f = 1; f <= block.floors; f += 1) {
        options.push(`${block.name} — Floor ${f}`);
      }
    }
  });
  return options;
};

export const createOnboardingData = ({
  siteId,
  siteType,
  numberOfBlocks,
  thumbnailPath,
  blocks,
}) => ({
  siteId,
  siteType,
  numberOfBlocks,
  thumbnailPath: thumbnailPath || null,
  blocks,
});

const blockTypesForStructure = kind => {
  if (kind === STRUCTURE_KINDS.VILLA_STREET) {
    return { blockType: 'street', flatType: 'villa' };
  }
  return { blockType: 'block', flatType: 'flat' };
};

const blockMetadataForApi = block => ({
  structure_kind: block.structureKind,
  floors: block.floors,
  basement_floors: block.basementFloors,
  flats_per_floor: block.flatsPerFloor,
  starting_flat_number: block.startingFlatNumber,
  block_prefix: block.blockPrefix,
  flat_pattern: block.flatPattern,
  lift_count: block.liftCount,
  staircase_count: block.staircaseCount,
  corridor_count: block.corridorCount,
  parking_floors: block.parkingFloors,
  terrace_access: block.terraceAccess,
  block_type: block.blockType,
  ...(block.villaGrouping ? { villa_grouping: block.villaGrouping } : {}),
  private_garden: block.privateGarden,
  private_parking: block.privateParking,
  backyard_area: block.backyardArea,
  water_meter: block.waterMeter,
  sewage_connection: block.sewageConnection,
  solar_connection: block.solarConnection,
  custom_features: (block.customFeatures || []).map(f => ({
    id: f.id,
    name: f.name,
    count: f.count,
  })),
  custom_unit_labels: block.customUnitLabels || {},
  removed_flat_keys: block.removedFlatKeys || [],
  extra_flats_per_floor: block.extraFlatsPerFloor || {},
});

const blockToBulkCreateEntry = block => {
  const types = blockTypesForStructure(block.structureKind);
  return {
    block_name: block.name,
    block_type: types.blockType,
    flat_type: types.flatType,
    flats: allFlatUnitLabelsForBlock(block),
    block_metadata: blockMetadataForApi(block),
  };
};

export const blocksFlatsBulkCreateBody = siteData => ({
  site: {
    site_type: siteData.siteType,
    number_of_blocks: siteData.numberOfBlocks,
    thumbnail_path: siteData.thumbnailPath,
    total_dwelling_units: totalFlatsForSite(siteData.blocks),
  },
  options: {
    idempotent: true,
    fail_fast: false,
    return_entities: true,
  },
  blocks: siteData.blocks.map(blockToBulkCreateEntry),
});

export const buildAmenitiesBulkUpsertBody = amenities => ({
  amenities: amenities.map(a => ({
    templateId: a.template.id,
    templateName: a.template.name,
    category: a.template.category,
    isCustom: a.template.isCustom || false,
    displayName: a.displayName,
    locationMapping: a.locationMapping,
    details: { ...(a.details || {}) },
  })),
});

export const siteTypeTitle = siteType => {
  switch (siteType) {
    case SITE_TYPES.VILLA:
      return 'Villa community';
    case SITE_TYPES.MIXED:
      return 'Mixed community';
    default:
      return 'Apartment community';
  }
};
